import React, { useEffect, useRef, useState } from "react";
import { BrandLogo } from "../components/brand/BrandLogo";
import { useParams } from "react-router-dom";
import axios from "axios";
import { PenLine, Eraser, CheckCircle2, Loader2, Home } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { tenant: "Locataire", owner: "Bailleur", referent: "Référent" };

export default function SignEdl() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => {
    axios.get(`${API}/edl/public/${token}`).then((r) => {
      setInfo(r.data); setName(r.data.party_name || ""); setDone(r.data.already_signed);
    }).catch(() => setErr("Lien invalide ou expiré."));
  }, [token]);

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.strokeStyle = "#121311"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.stroke(); hasDrawn.current = true; };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };

  const submit = async () => {
    if (!name.trim()) { toast.error("Veuillez indiquer votre nom"); return; }
    if (!hasDrawn.current) { toast.error("Veuillez signer dans le cadre"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/edl/public/${token}/sign`, { signer_name: name.trim(), signature_png_b64: canvasRef.current.toDataURL("image/png") });
      setDone(true);
      toast.success("Signature enregistrée. Merci !");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (err) return <Centered><p className="text-red-600 font-medium">{err}</p></Centered>;
  if (!info) return <Centered><Loader2 className="w-8 h-8 animate-spin text-brand-green/50" /></Centered>;

  return (
    <Centered>
      <div className="bg-white rounded-3xl border border-brand-soft shadow-xl w-full max-w-lg p-7" data-testid="sign-edl-page">
        <div className="flex items-center gap-2 mb-1"><BrandLogo className="h-8 w-auto" /></div>
        <h1 className="font-display text-2xl font-semibold text-brand-greenDeep mt-3">Signature de l'état des lieux</h1>
        <p className="text-sm text-brand-green/60 mt-1 flex items-center gap-1"><Home className="w-4 h-4" />{info.property_name} · {info.type === "sortie" ? "Sortie" : "Entrée"} · {info.edl_date}</p>
        <p className="text-sm text-brand-green/60 mt-1">Vous signez en tant que <b>{LABELS[info.party]}</b>.</p>

        {done ? (
          <div className="mt-6 text-center py-8" data-testid="sign-edl-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <p className="font-semibold text-brand-greenDeep mt-3">Merci, votre signature a bien été enregistrée.</p>
            <p className="text-sm text-brand-green/55">Vous pouvez fermer cette page.</p>
          </div>
        ) : (
          <div className="mt-5">
            <label className="block mb-3">
              <span className="text-xs font-medium text-brand-green/70 mb-1 block">Votre nom</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm" data-testid="sign-edl-name" />
            </label>
            <canvas ref={canvasRef} width={460} height={200} className="w-full border-2 border-dashed border-brand-soft rounded-xl bg-brand-softer/40 touch-none" data-testid="sign-edl-canvas"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
            <div className="flex items-center justify-between mt-3">
              <button onClick={clear} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold"><Eraser className="w-4 h-4" />Effacer</button>
              <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="sign-edl-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}Signer
              </button>
            </div>
          </div>
        )}
      </div>
    </Centered>
  );
}

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-brand-softer p-4">{children}</div>
);
